import moment from "moment/moment";
import { useCallback, useState } from "react";
import ProjectUtils from "../../../../utils/project-utils";
import NotificationsService from "../../../../services/web/notification-service";
import { FaArrowLeft, FaExclamationTriangle, FaInfoCircle, FaPhoneAlt } from "react-icons/fa";
import { Card } from "../../../../components/card/card";
import NotificationPassages from "./notification-passages";
import NotificationMap from "./notification-map";
import Button from "../../../../components/button/button";
import { Modal } from "../../../../components/modal/modal";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { toast } from "react-toastify";

const initialModal = { show: false, src: undefined };

export default function NotificationDetails({ selected, returnToDefault, mapsKey, target, hasDispatch }) {
    const { id, view, dtEvent, dtCreate, sign, relationship, title, delayed, origin } = selected;
    const [modal, setModal] = useState(initialModal);
    const [modalHistory, setModalHistory] = useState(false);

    const diff = moment().diff(moment(dtEvent), "days");
    const dateFormatted = moment(dtEvent).format("DD/MM/YYYY HH:mm:ss");

    const handleOccurrence = () => {
        if (!relationship || !relationship.occurrence) {
            ProjectUtils.createOccurrence(id, "/notifications/create-occurrence");
            return;
        };

        ProjectUtils.openOccurrence(relationship.occurrence);
    };

    const handleAttendance = useCallback(() => {
        const attendanceId = relationship?.attendance;
        const url = attendanceId
            ? NotificationsService.attendanceEditUrl(attendanceId)
            : NotificationsService.attendanceCreateUrl(id);

        window.open(url);
    }, [relationship?.attendance, id]);

    const markAsSigned = async () => {
        const formData = new FormData();
        formData.append("id", String(selected.id));

        try {
            await NotificationsService.markAsSigned(formData);
            toast.success("Assinado com sucesso");
        } catch (error) {
            console.error(error);
            ProjectUtils.swalErrorMessage("", "Não foi possível assinar.", "Ok");
        };
    };

    const openModal = (src) => setModal({ show: true, src });

    const closeModal = () => setModal({ show: false, src: undefined });

    const openAnalise = () => {
        NotificationsService.openAnalise()
            .then(() => {
                toast.success("A análise será aberta no SentryView");
            })
            .catch(error => {
                console.error(error);
                ProjectUtils.swalErrorMessage("", "Ocorreu um erro ao abrir a análise no SentryView.", "Ok");
            });
    }

    const timeLine = [
        {
            dt: dateFormatted,
            text: "Data/hora do evento",
            icon: NotificationsService.iconBase("/server-processing.png"),
        },
        {
            dt: moment(dtCreate).format("DD/MM/YYYY HH:mm:ss"),
            text: "Data/hora da notificação" + (delayed ? " (atrasada)" : ""),
            icon: NotificationsService.iconBase("/server-ok.png"),
            delayed: delayed,
        },
        {
            dt: sign ? moment(selected.signDateTime).format("DD/MM/YYYY HH:mm:ss") : null,
            text: "Data/hora da assinatura",
            icon: NotificationsService.iconBase("/signing.png"),
        },
    ];

    return (
    <>
        <div className="flex h-full flex-col min-h-0 overflow-hidden">
            <div className="mb-2 flex shrink-0 items-start justify-between gap-3">
                <h2 className="m-0 flex min-h-0 items-center gap-4 truncate text-2xl! font-bold">
                    <span className="cursor-pointer" onClick={() => returnToDefault(selected.id)} >
                        <FaArrowLeft />
                    </span>
                    {title}
                </h2>
                <div 
                    className={[
                        "flex shrink-0 cursor-pointer items-center",
                        delayed ? "text-red-600" : "text-sky-600",
                    ].join(" ")}
                    onClick={() => setModalHistory(true)}
                    title="Linha do tempo"
                >
                    <span className="m-2">
                        {delayed ? (
                            <FaExclamationTriangle className="text-red-700" />
                        ) : (
                            <FaInfoCircle className="text-sky-400" />
                        )}
                    </span>
                    {delayed ? (
                        <span className="whitespace-nowrap text-red-700">
                        {dateFormatted} {diff > 1 ? `(há ${diff} dias)` : ""}
                        </span>
                    ) : (
                        <span className="whitespace-nowrap text-zinc-500 font-medium">
                        {dateFormatted} {diff > 1 ? `(há ${diff} dias)` : ""}
                        </span>
                    )}
                </div>
            </div>
            
            <div className="flex-1 min-h-0 overflow-hidden">
                <Card className={"h-full min-h-0 border-zinc-200 p-1"}>
                    <Card.Body className={"h-full min-h-0 overflow-y-auto space-y-4"}>
                        {[...view]
                            .sort((a, b) => a.index - b.index)
                            .map((item, i) => {
                                switch (item.type) {
                                    case "text" :
                                        return (
                                            <p key={id + "-" + i}>
                                                {item.name}: <b>{item.value}</b>
                                            </p>
                                        );
                                    case "datetime" :
                                        return (
                                            <p key={id + "-" + i}>
                                                {item.name}: <b>{moment(item.value).format("DD/MM/YYYY HH:mm:ss")}</b>
                                            </p>
                                        );
                                    case "vehicle_passage" :
                                        return (
                                            <section key={"passage-" + id + "-" + i}>
                                                <NotificationPassages 
                                                    passage={item.value}
                                                    name={item.name}
                                                    openModal={openModal}
                                                />
                                            </section>
                                        );
                                    case "map_points" :
                                        return (
                                            <section key={"map-" + id + "-" + i}>
                                                <NotificationMap 
                                                    markers={item.value}
                                                    name={item.name}
                                                    title={title}
                                                    apiKey={mapsKey}
                                                    id={id}
                                                />
                                            </section>
                                        );
                                    default:
                                        return null;
                                }
                            })
                        }
                    </Card.Body>
                </Card>
            </div>
            
            <div className="mt-2 flex shrink-0 items-center justify-end gap-2 pb-px">
                {(target !== "signed" || relationship?.occurrence) && (
                    <Button
                        title={relationship?.occurrence ? "Abrir ocorrência" : "Criar ocorrência"}
                        target={relationship?.occurrence ? "primary" : "primary-outline"}
                        onClick={handleOccurrence}
                    >
                        <img 
                            className="h-5 w-5"
                            src={NotificationsService.occurrenceIcon(!!relationship?.occurrence)}
                            alt="sentry.occurrence"
                        />
                    </Button>
                )}
                {hasDispatch && (target !== "signed" || relationship?.attendance) && (
                    <Button
                        target={relationship?.attendance ? "primary" : "primary-outline"}
                        title={relationship?.attendance ? "Abrir atendimento" : "Criar atendimento"}
                        onClick={handleAttendance}
                    >
                        <FaPhoneAlt />
                    </Button>
                )}
                {
                    ["jammer", "rolezinho"].includes(origin) &&
                    <Button onClick={openAnalise}>
                        Análises
                    </Button>
                }
                {sign ? (
                    <p className="relative top-1">
                        Assinado por{" "}
                        <b>{selected.signUser}</b> em {" "}
                        <b>{moment(selected.signDateTime).format("DD/MM/YYYY HH:mm:ss")}</b>
                    </p>
                ) : (
                    <Button
                        onClick={markAsSigned}
                        className={"px-20"}
                        actionFeedbackType="save"
                        loadingText="Assinando..."
                        successText="Assinado com sucesso"
                    >
                        Assinar
                    </Button>
                )}
            </div>
        </div>

        <Modal
            open={modal.show}
            setOpen={(open) => { if (!open) closeModal(); }}
            title={""}
            showCloseButton={true}
            overflow={true}
            className="w-full max-w-5xl px-2 bg-transparent shadow-none"
            headerClass="border-0 p-0"
        >
            <Modal.Body className="h-[90vh] bg-transparent p-0">
                <TransformWrapper>
                    <TransformComponent>
                        {modal?.src && (
                            <div 
                                className="h-full w-full bg-center bg-contain bg-no-repeat cursor-zoom-in select-none" 
                                style={{ backgroundImage: `url("${modal.src}")` }}
                            />
                        )}
                    </TransformComponent>
                </TransformWrapper>
            </Modal.Body>
        </Modal>
 
        <Modal
            open={modalHistory}
            setOpen={setModalHistory}
            title="Linha do tempo"
            className="w-full max-w-3xl px-2"
        >
            <Modal.Body className={"py-5"}>
                {(() => {
                    const items = timeLine.filter((item) => !!item.dt);
                    return (
                        <div
                            className={[
                                "relative space-y-3",
                                "before:report-[''] before:absolute before:left-[calc(8.75rem+1.5rem+22px)]",
                                "before:top-5.5 before:bottom-5.5 before:w-1 before:rounded before:bg-slate-700",
                            ].join(" ")}
                        >
                            {items.map((item, index) => {
                                const [datePart, timePart] = String(item.dt).split(" ");

                                return (
                                    <div key={"timeline-" + index} className="flex items-center gap-6">
                                        <div className="w-35.5 text-right text-zinc-500 font-semibold leading-tight text-base">
                                            <div>{datePart}</div>
                                            <div>{timePart}</div>
                                        </div>

                                        <div className="relative flex w-11 flex-col items-center">
                                            <div 
                                                className={[
                                                    "relative z-10 flex h-11 w-11 items-center justify-center rounded-full border-3 border-zinc-200",
                                                    item?.delayed ? "bg-red-600" : "bg-slate-700",
                                                ].join(" ")}
                                                title={item?.delayed ? "Atrasado" : "No prazo"}
                                            >
                                                <img 
                                                    src={item.icon}
                                                    alt=""
                                                    className="h-5 w-5 object-contain select-none"
                                                    draggable={false}
                                                />
                                            </div>
                                        </div>

                                        <div
                                            className={[
                                                "relative flex-1 mr-10 rounded-md bg-zinc-100 px-6 py-4 text-zinc-500 text-base font-semibold",
                                                "before:report-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2",
                                                "before:-translate-x-2 before:border-y-10 before:border-y-transparent before:border-r-10 before:border-r-slate-100",
                                            ].join(" ")}
                                        >
                                            {item.text}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </Modal.Body>
        </Modal>
    </>
    )
}